import React, { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import { useSelector, useDispatch } from "react-redux";
import ActionButton from "../../ActionButton";
import {
  CardHeader,
  IconButton,
  CardContent,
  TextField,
  Select,
  FormControl,
  CircularProgress,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  MenuItem,
  Checkbox,
  Button,
  FormGroup,
  Typography,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Tabs,
  Tab,
  Divider,
  FormHelperText,
  Collapse,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import axios from "../../../AxiosClient";
import PropertyNames from "../../../PropertyNames";
import { setApplicationProperties } from "../../../redux/actions/universalActions.js";
import { v4 } from "uuid";
import { currentUserHasRole, Role } from "../../../utils/RoleUtils";

const useStyles = makeStyles((theme) => ({
  input: {
    marginBottom: 10,
  },
  spinnerContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: "5px auto",
  },
  templatesContainer: {
    display: "flex",
    flexDirection: "column",
  },
}));

function SidebarPDF(props) {
  const [controller, setController] = useState(new AbortController());
  const { t } = useTranslation();
  const [filename, setFilename] = useState("document");
  const [scale, setScale] = useState(1);
  const [format, setFormat] = useState("A4");
  const [landscape, setLandscape] = useState(false);
  const [displayHeaderFooter, setDisplayHeaderFooter] = useState(false);
  const [processing, setProcessing] = useState(false);
  const templates = useSelector((state) => {
    if (state.setting.applicationProperties[PropertyNames.PDF_TEMPLATES]) {
      return JSON.parse(
        state.setting.applicationProperties[PropertyNames.PDF_TEMPLATES]
      );
    } else {
      return [];
    }
  });
  const [template, setTemplate] = useState({});
  const [editTemplates, setEditTemplates] = useState(false);
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(
    () => {
      if (templates && !template.name) {
        let defaults = templates.filter((t) => t.default);
        if (defaults.length > 0) {
          setTemplate(defaults[0]);
        } else {
          setTemplate({ name: "None", displayHeaderFooter: false });
        }
      }
    },
    [templates]
  );

  useEffect(
    () => {
      if (
        props.internalDocument &&
        props.internalDocument.contentRVO &&
        props.internalDocument.contentRVO.title
      ) {
        setFilename(props.internalDocument.contentRVO.title);
      }
    },
    [props.internalDocument]
  );

  const cancelExport = () => {
    controller.abort();
    setController(new AbortController());
    setProcessing(false);
  };

  const exportToPdf = () => {
    setProcessing(true);
    let api = `https://${window.location.hostname}:8081`;
    if (!process.env.NODE_ENV || process.env.NODE_ENV === "production") {
      api = `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? ":" + window.location.port : ""
      }`;
    }
    fetch(`${api}/pdf/${props.internalDocument.documentRVO.documentID}`, {
      method: "POST",
      credentials: "include",
      signal: controller.signal,
      responseType: "blob",
      headers: {
        // Authorization: "Bearer " + localStorage.getItem("access_token"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        scale,
        format,
        landscape,
        ...template,
      }),
    })
      .then((res) => {
        if (res.ok) {
          res.blob().then((blob) => {
            setProcessing(false);
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `${filename}.pdf`;
            link.click();
          });
        } else {
          setProcessing(false);
          enqueueSnackbar(t("pdf_export_error_occurred"), { variant: "error" });
        }
      })
      .catch((err) => {
        setProcessing(false);
        if (err.name !== "AbortError") {
          enqueueSnackbar(t("pdf_export_error_occurred"), { variant: "error" });
        }
      });
  };

  return (
    <>
      {props.show && (
        <>
          <CardHeader
            title={t("sidebar_pdf_export")}
            titleTypographyProps={{ component: "h3" }}
            action={
              <>
                {props.toggleSidebar && (
                  <IconButton
                    onClick={props.toggleSidebar}
                    aria-label={"Close sidebar"}
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </>
            }
          />
          <CardContent style={{ display: "flex", flexDirection: "column" }}>
            <div className={classes.spinnerContainer}>
              {processing ? (
                <>
                  <CircularProgress
                    variant="indeterminate"
                    style={{ marginBottom: 5 }}
                  />
                  <Typography gutterBottom>{t("pdf_exporting")}</Typography>
                  <Typography>{t("pdf_exporting_take_few_seconds")}</Typography>
                </>
              ) : (
                <Typography gutterBottom>{t("pdf_export_header")}</Typography>
              )}
            </div>
            <div style={{ margin: "10px auto 30px" }}>
              <Button
                id="sidebar-pdf-export"
                variant="contained"
                color={processing ? "secondary" : "primary"}
                onClick={() => (processing ? cancelExport() : exportToPdf())}
              >
                {processing ? t("pdf_cancel_export") : t("pdf_do_export")}
              </Button>
            </div>
            <Typography gutterBottom variant="body2" />
            <FormControl className={classes.input}>
              <InputLabel id="pdf-template">{t("pdf_template")}</InputLabel>
              <Select
                labelId="pdf-template"
                value={template.name}
                onChange={(e) => {
                  if (e.target.value === "None") {
                    setTemplate({
                      name: "None",
                      css: "",
                      footer: " ",
                      header: " ",
                    });
                  } else {
                    setTemplate(
                      templates.filter((t) => t.name === e.target.value)[0]
                    );
                  }
                }}
                disabled={processing}
              >
                <MenuItem value="None">None</MenuItem>
                {templates.map((t, idx) => (
                  <MenuItem key={idx} value={t.name}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {" "}
                {currentUserHasRole(Role.MODERATOR)
                  ? t("pdf_template_sidebar_text")
                  : t("pdf_template_sidebar_text_cannot_edit")}
              </FormHelperText>
            </FormControl>
            {currentUserHasRole(Role.MODERATOR) && (
              <Button
                variant="outlined"
                disabled={processing}
                style={{ marginBottom: 10 }}
                onClick={() => {
                  setEditTemplates(true);
                  setTemplate({
                    name: "None",
                    css: "",
                    footer: " ",
                    header: " ",
                  });
                }}
              >
                {t("pdf_edit_templates")}
              </Button>
            )}
            {editTemplates && (
              <EditTemplates
                open={editTemplates}
                onClose={() => setEditTemplates(false)}
              />
            )}
            <TextField
              label={t("pdf_file_name")}
              className={classes.input}
              disabled={processing}
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
            />
            <FormControl component="fieldset" className={classes.input}>
              <FormLabel component="legend">
                {t("pdf_page_orientation")}
              </FormLabel>
              <RadioGroup
                disabled={processing}
                row
                aria-label="orientation"
                value={landscape ? "landscape" : "portrait"}
                onChange={(e) => setLandscape(e.target.value === "landscape")}
              >
                <FormControlLabel
                  value="portrait"
                  control={<Radio disabled={processing} />}
                  label="Portrait"
                />
                <FormControlLabel
                  value="landscape"
                  control={<Radio disabled={processing} />}
                  label="Landscape"
                />
              </RadioGroup>
            </FormControl>
            <FormControl className={classes.input}>
              <InputLabel id="page-size-select">
                {t("pdf_page_size")}
              </InputLabel>
              <Select
                labelId="page-size-select"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                disabled={processing}
              >
                <MenuItem value="Letter">Letter</MenuItem>
                <MenuItem value="Legal">Legal</MenuItem>
                <MenuItem value="Tabloid">Tabloid</MenuItem>
                <MenuItem value="Ledger">Ledger</MenuItem>
                <MenuItem value="A0">A0</MenuItem>
                <MenuItem value="A1">A1</MenuItem>
                <MenuItem value="A2">A2</MenuItem>
                <MenuItem value="A3">A3</MenuItem>
                <MenuItem value="A4">A4</MenuItem>
                <MenuItem value="A5">A5</MenuItem>
                <MenuItem value="A6">A6</MenuItem>
              </Select>
            </FormControl>
            <TextField
              className={classes.input}
              label={t("pdf_scale")}
              disabled={processing}
              value={scale}
              onChange={(e) => {
                setScale(e.target.value);
              }}
              type="number"
              inputProps={{ step: "0.1", min: 0.1, max: 2 }}
              helperText={t("pdf_scale_helper_text")}
            />
          </CardContent>
        </>
      )}
    </>
  );
}

const useStylesEditTemplates = makeStyles((theme) => ({
  templatesContainer: {
    display: "flex",
    flexDirection: "column",
  },
}));
function EditTemplates(props) {
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const { t } = useTranslation();
  const classes = useStylesEditTemplates();
  const [selected, setSelected] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const properties = useSelector((state) => {
    return {
      ...state.setting.applicationProperties,
    };
  });
  const templates = useSelector((state) => {
    if (state.setting.applicationProperties[PropertyNames.PDF_TEMPLATES]) {
      return JSON.parse(
        state.setting.applicationProperties[PropertyNames.PDF_TEMPLATES]
      );
    } else {
      return [];
    }
  });
  const dispatch = useDispatch();

  const updateTemplates = (newTemplates) => {
    dispatch(
      setApplicationProperties({
        ...properties,
        [PropertyNames.PDF_TEMPLATES]: newTemplates,
      })
    );
  };

  const deleteTemplate = () => {
    setSaving(true);
    let index = templates.findIndex((t) => t.id === selected.id);
    if (index > -1) {
      let copy = [...templates];
      copy.splice(index, 1);
      axios
        .patch("/application-property/", {
          [PropertyNames.PDF_TEMPLATES]: JSON.stringify(copy),
        })
        .then((res) => {
          enqueueSnackbar(t("pdf_template_deleted"), { variant: "success" });
          if (res.data[PropertyNames.PDF_TEMPLATES]) {
            updateTemplates(res.data[PropertyNames.PDF_TEMPLATES]);
          }
          setSelected(null);
          setSaving(false);
        })
        .catch(() => {
          enqueueSnackbar(t("error_occurred_generic"), { variant: "error" });
          setSaving(false);
        });
    } else {
      enqueueSnackbar(t("error_occurred_generic"), { variant: "error" });
      setSaving(false);
    }
  };

  const save = () => {
    setSaving(true);
    let toSave = [...templates];
    if (toSave.length === 0) {
      toSave = [selected];
    } else if (toSave.filter((t) => t.id === selected.id).length === 0) {
      toSave.push(selected);
    } else {
      toSave = toSave.map((t) => {
        if (t.id === selected.id) {
          return selected;
        }
        if (selected.default) {
          return { ...t, default: false };
        }
      });
    }
    axios
      .patch("/application-property/", {
        [PropertyNames.PDF_TEMPLATES]: JSON.stringify(toSave),
      })
      .then((res) => {
        enqueueSnackbar(t("pdf_template_saved"), { variant: "success" });
        if (res.data[PropertyNames.PDF_TEMPLATES]) {
          updateTemplates(res.data[PropertyNames.PDF_TEMPLATES]);
        }
        setSaving(false);
      })
      .catch(() => {
        enqueueSnackbar(t("error_occurred_generic"), { variant: "error" });
        setSaving(false);
      });
  };

  const getDialogBody = (tab) => {
    let title;
    let label;
    let value;
    let key;
    let heightLabel;
    let heightKey;
    if (tab === "header") {
      title = (
        <Trans
          i18nKey="pdf_header_subtitle"
          components={{ bold: <strong /> }}
        />
      );
      label = t("pdf_header_template");
      value = selected.headerTemplate;
      key = "headerTemplate";
      heightLabel = t("pdf_header_height");
      heightKey = "headerHeight";
    } else if (tab === "footer") {
      title = (
        <Trans
          i18nKey="pdf_footer_subtitle"
          components={{ bold: <strong /> }}
        />
      );
      label = t("pdf_footer_template");
      value = selected.footerTemplate;
      key = "footerTemplate";
      heightLabel = t("pdf_footer_height");
      heightKey = "footerHeight";
    } else if (tab === "settings") {
      return (
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={selected.flattenSections}
                onChange={(e) =>
                  setSelected({
                    ...selected,
                    flattenSections: e.target.checked,
                  })
                }
                name="flattenSections"
                color="primary"
              />
            }
            label={t("pdf_flatten_sections")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={selected.hideTitle}
                onChange={(e) =>
                  setSelected({
                    ...selected,
                    hideTitle: e.target.checked,
                  })
                }
                name="hideTitle"
                color="primary"
              />
            }
            label={t("pdf_hide_title")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={selected.hideSectionTitles}
                onChange={(e) =>
                  setSelected({
                    ...selected,
                    hideSectionTitles: e.target.checked,
                  })
                }
                name="hideSectionTitles"
                color="primary"
              />
            }
            label={t("pdf_hide_section_titles")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={selected.displayHeaderFooter}
                onChange={(e) =>
                  setSelected({
                    ...selected,
                    displayHeaderFooter: e.target.checked,
                  })
                }
                name="displayHeaderFooter"
                color="primary"
              />
            }
            label={t("pdf_show_header_footer")}
          />
        </FormGroup>
      );
    }
    return (
      <>
        <Typography gutterBottom style={{ marginTop: 5 }}>
          {title}
        </Typography>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <TextField
            label={heightLabel}
            name="headerFooterHeight"
            inputProps={{
              min: "0",
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
            }}
            value={selected[heightKey]}
            onChange={(e) =>
              setSelected({
                ...selected,
                [heightKey]: e.target.value,
              })
            }
            type="number"
          />
          <TextField
            InputProps={{ style: { fontFamily: "monospace" } }}
            rows={8}
            multiline
            label={label}
            value={value}
            onChange={(e) => {
              setSelected({ ...selected, [key]: e.target.value });
            }}
          />
        </div>
      </>
    );
  };
  const nameTaken =
    selected && templates.filter((t) => t.name === selected.name).length > 0;
  const existing = nameTaken
    ? templates.filter((t) => t.name === selected.name)[0]
    : null;
  return (
    <Dialog open={props.open} fullWidth>
      <DialogTitle>{t("pdf_template_edit_dialog_header")}</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>{t("template_dialog_subheader")}</Typography>
        <div style={{ textAlign: "center" }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelected({
                id: v4(),
                name: "",
                headerTemplate: " ",
                footerTemplate: " ",
                displayHeaderFooter: true,
                default: false,
                hideTitle: false,
                flattenSections: false,
                hideSectionTitles: false,
                headerHeight: 70,
                footerHeight: 70,
              });
            }}
          >
            {t("pdf_new_template")}
          </Button>
        </div>
        <Typography gutterBottom style={{ marginTop: 10 }}>
          {t("template_dialog_or_edit_existing")}
        </Typography>
        <FormControl fullWidth>
          <InputLabel id="pdf-template">{t("pdf_template")}</InputLabel>
          <Select
            labelId="pdf-template"
            value={selected ? selected.id : ""}
            onChange={(e) =>
              setSelected(templates.filter((t) => t.id === e.target.value)[0])
            }
          >
            <MenuItem value="" />
            {templates &&
              templates.map((t, idx) => (
                <MenuItem key={idx} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <div className={classes.templatesContainer}>
          <Collapse in={!!selected}>
            {!!selected && (
              <>
                <Divider style={{ marginTop: 20, marginBottom: 10 }} />
                <TextField
                  label={t("template_name")}
                  value={selected.name}
                  fullWidth
                  required
                  onChange={(e) =>
                    setSelected({ ...selected, name: e.target.value })
                  }
                  error={nameTaken && existing && existing.id !== selected.id}
                  helperText={
                    nameTaken && existing && existing.id !== selected.id
                      ? t("pdf_template_name_taken")
                      : t("global_required")
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selected.default}
                      onChange={(e) =>
                        setSelected({
                          ...selected,
                          default: e.target.checked,
                        })
                      }
                      name="default"
                      color="primary"
                    />
                  }
                  label={t("pdf_template_make_default")}
                />
                <Tabs value={tab} onChange={(e, nv) => setTab(nv)}>
                  <Tab label={t("pdf_settings")} />
                  <Tab label={t("pdf_header")} />
                  <Tab label={t("pdf_footer")} />
                </Tabs>
                {tab === 0 && getDialogBody("settings")}
                {tab === 1 && getDialogBody("header")}
                {tab === 2 && getDialogBody("footer")}
                <div style={{ marginTop: 10, display: "flex" }}>
                  {templates.length > 0 &&
                    templates.filter((t) => t.id === selected.id).length >
                      0 && (
                      <ActionButton
                        loading={saving}
                        style={{ color: "red", marginRight: 5 }}
                        onClick={deleteTemplate}
                        variant="outlined"
                      >
                        {t("pdf_delete_template")}
                      </ActionButton>
                    )}
                  <div style={{ marginLeft: "auto" }}>
                    <ActionButton
                      loading={saving}
                      onClick={save}
                      variant="outlined"
                      disabled={
                        !selected.name ||
                        (nameTaken && existing && existing.id !== selected.id)
                      }
                    >
                      {t("global_save")}
                    </ActionButton>
                  </div>
                </div>
              </>
            )}
          </Collapse>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>{t("global_close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default SidebarPDF;
